import { Job, SupabaseJob } from '@applicator/shared'

export type RelevanceFilter = 'all' | 'relevant' | 'not-relevant'

// Helper function to filter jobs by search query
export function filterBySearchQuery(jobsToFilter: Job[], query: string): Job[] {
  if (!query.trim()) return jobsToFilter
  
  const searchLower = query.toLowerCase().trim()
  return jobsToFilter.filter((job) => {
    const positionMatch = job.position?.toLowerCase().includes(searchLower) || false
    const companyMatch = job.company?.toLowerCase().includes(searchLower) || false
    return positionMatch || companyMatch
  })
}

// Helper function to check if job is relevant
function isJobRelevant(supabaseJob: SupabaseJob | undefined): boolean {
  if (supabaseJob?.rejected_from_relevant) return false
  return supabaseJob?.is_relevant === true || supabaseJob?.relevance_verdict === 'Fit'
}

// Helper function to check if job is not relevant
function isJobNotRelevant(supabaseJob: SupabaseJob | undefined): boolean {
  return supabaseJob?.is_relevant === false || supabaseJob?.relevance_verdict === 'No Fit'
}

// Helper function to find supabase job for filtering
function findSupabaseJobForFilter(jobId: string, supabaseJobs: SupabaseJob[]): SupabaseJob | undefined {
  return supabaseJobs.find(j => j.id === jobId)
}

// Helper function to check if filter is all
function isFilterAll(filter: RelevanceFilter): boolean {
  return filter === 'all'
}

// Helper function to check if filter is relevant
function isFilterRelevant(filter: RelevanceFilter): boolean {
  return filter === 'relevant'
}

// Helper function to check if filter is not relevant
function isFilterNotRelevant(filter: RelevanceFilter): boolean {
  return filter === 'not-relevant'
}

// Helper function to get job ID for filtering
function getJobIdForFiltering(job: Job): string {
  return job.id || ''
}

// Helper function to check relevance for relevant filter
function checkRelevanceForRelevantFilter(supabaseJob: SupabaseJob | undefined): boolean {
  return isJobRelevant(supabaseJob)
}

// Helper function to check relevance for not relevant filter
function checkRelevanceForNotRelevantFilter(supabaseJob: SupabaseJob | undefined): boolean {
  return isJobNotRelevant(supabaseJob)
}

// Helper function to check relevance for filter
function checkRelevanceForFilter(
  filter: RelevanceFilter,
  supabaseJob: SupabaseJob | undefined
): boolean {
  if (isFilterRelevant(filter)) {
    return checkRelevanceForRelevantFilter(supabaseJob)
  }
  if (isFilterNotRelevant(filter)) {
    return checkRelevanceForNotRelevantFilter(supabaseJob)
  }
  return true
}

// Helper function to check if job matches relevance filter
function matchesRelevanceFilter(job: Job, filter: 'all' | 'relevant' | 'not-relevant', supabaseJobs: SupabaseJob[]): boolean {
  if (isFilterAll(filter)) return true
  
  const jobId = getJobIdForFiltering(job)
  const supabaseJob = findSupabaseJobForFilter(jobId, supabaseJobs)
  
  return checkRelevanceForFilter(filter, supabaseJob)
}

// Helper function to filter jobs by relevance
export function filterByRelevance(jobsToFilter: Job[], filter: RelevanceFilter, supabaseJobs: SupabaseJob[]): Job[] {
  if (isFilterAll(filter)) return jobsToFilter
  return jobsToFilter.filter((job) => matchesRelevanceFilter(job, filter, supabaseJobs))
}
